import React, { useEffect, useRef } from 'react';

/**
 * The "AI is working" field — the web twin of the app's loading moment.
 *
 * THE RULE THAT MAKES IT LOOK REAL: speed only ever DECREASES. It starts fast
 * and settles through a fixed ladder, exactly as the app does. Anything that
 * pulses on a loop reads as decoration — real processing slows as it converges,
 * and a spinner that beats like a metronome is the tell that nothing is
 * actually happening.
 *
 * Drawn on a canvas rather than with DOM nodes: a few hundred moving elements
 * as divs means a few hundred layout objects, and the whole point is that this
 * appears while the machine is already busy with a request.
 */

/** [elapsed ms, speed]. Monotonically decreasing — matches the app's ladder. */
const SPEED_LADDER: [number, number][] = [
  [0, 5.5],
  [1100, 3.4],
  [2600, 2.3],
  [4800, 1.5],
  [8000, 1.0],
  [12000, 0.75],
];

const speedAt = (ms: number) => {
  let s = SPEED_LADDER[0][1];
  for (const [at, v] of SPEED_LADDER) if (ms >= at) s = v;
  return s;
};

interface Props {
  /** Height of the field. */
  height?: number;
  className?: string;
}

export const ThinkingField: React.FC<Props> = ({ height = 220, className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Someone who asked for less motion gets a still field, not a blank box.
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // A De Jong attractor, the same family the app draws. It gives structured
    // drift — points that wander with a shape to them rather than random noise,
    // which is what makes it read as thought rather than static.
    const A = 1.641, B = 1.902, C = 0.316, D = 1.525;
    const COUNT = Math.min(420, Math.round(w * 0.55));

    const pts = Array.from({ length: COUNT }, () => ({
      x: Math.random() * 2 - 1,
      y: Math.random() * 2 - 1,
      // Three depth layers, as in the app's plexus config.
      depth: [1, 0.7, 0.45][Math.floor(Math.random() * 3)],
    }));

    const start = performance.now();
    let raf = 0;

    const frame = (now: number) => {
      const elapsed = now - start;
      const speed = reduced ? 0 : speedAt(elapsed) * 0.0016;

      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const scale = Math.min(w, h) * 0.29;

      for (const p of pts) {
        if (speed > 0) {
          const nx = Math.sin(A * p.y) - Math.cos(B * p.x);
          const ny = Math.sin(C * p.x) - Math.cos(D * p.y);
          p.x += (nx - p.x) * speed;
          p.y += (ny - p.y) * speed;
        }

        const px = cx + p.x * scale;
        const py = cy + p.y * scale;
        const r = 0.9 + p.depth * 1.7;

        // Violet is the AI thinking; the glacier tint on the nearest layer is
        // what gives the field depth rather than a flat dust cloud.
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fillStyle =
          p.depth > 0.9
            ? `rgba(162,155,255,${0.55 * p.depth})`
            : `rgba(97,92,237,${0.5 * p.depth})`;
        ctx.fill();
      }

      if (!reduced) raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      role="presentation"
      aria-hidden="true"
      style={{ height, width: '100%', display: 'block' }}
      className={className}
    />
  );
};

export default ThinkingField;
